import { useState } from "react";
import { ArrowLeft, CheckCircle2, Eye, EyeOff, KeyRound, Loader2, MailCheck, ShieldCheck } from "lucide-react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errText } from "../../connect/api";

const Frame = ({ children, title, sub }) => (
  <div className="min-h-full flex items-center justify-center px-6 py-10 bg-[#070b14] nxc-fade-in">
    <div className="w-full max-w-[420px]">
      <div className="flex items-center gap-3 mb-8"><img src="/nsconnect/logo-512.png" alt="" className="w-11 h-11 rounded-full" /><div><div className="nxc-display font-bold">NexSecure <span className="text-blue-400">Connect</span></div><div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Account recovery</div></div></div>
      <h1 className="nxc-display text-3xl font-semibold mb-1">{title}</h1>
      <p className="text-sm text-slate-400 mb-6">{sub}</p>
      {children}
      <Link to="/connect" className="mt-8 inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-200" data-testid="recovery-back-to-login"><ArrowLeft size={13} /> Back to sign in</Link>
    </div>
  </div>
);

export const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!/@nexsecure\.in$/i.test(email.trim())) { toast.error("Enter your @nexsecure.in email address"); return; }
    setBusy(true);
    try { await api.post("/auth/forgot", { email: email.trim() }); setSent(true); } catch (err) { toast.error(errText(err)); } finally { setBusy(false); }
  };
  return (
    <Frame title="Forgot password" sub="We'll email you a one-time reset link from hr@nexsecure.in. It works once and expires in 1 hour.">
      {sent ? (
        <div className="nxc-card p-4 flex gap-3 items-start" data-testid="forgot-sent"><MailCheck size={20} className="text-emerald-300 shrink-0 mt-0.5" /><div className="text-sm text-slate-200">If <b>{email}</b> is registered on NexSecure Connect, a reset link is on its way. Check your inbox (and spam). You can request another link after 10 minutes.</div></div>
      ) : (
        <form onSubmit={submit} className="space-y-3" data-testid="forgot-form">
          <input type="email" className="nxc-input" placeholder="you@nexsecure.in" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" data-testid="forgot-email-input" />
          <button type="submit" disabled={busy || !email} className="nxc-btn nxc-btn-primary w-full h-12" data-testid="forgot-submit-button">{busy ? <Loader2 size={16} className="animate-spin" /> : <KeyRound size={16} />} Email me a reset link</button>
        </form>
      )}
    </Frame>
  );
};

export const ResetPassword = () => {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [cf, setCf] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    if (pw !== cf) { toast.error("Passwords do not match"); return; }
    if (pw.length < 8 || !/\d/.test(pw) || !/[a-zA-Z]/.test(pw)) { toast.error("Use at least 8 characters with letters and numbers"); return; }
    setBusy(true);
    try { const { data } = await api.post("/auth/reset", { token, new: pw }); setDone(data.email); } catch (err) { toast.error(errText(err)); } finally { setBusy(false); }
  };
  if (!token) return <Frame title="Invalid link" sub="This reset link is missing its code. Request a new one from the sign-in page." />;
  return (
    <Frame title="Choose a new password" sub="At least 8 characters with letters and numbers.">
      {done ? (
        <div className="space-y-4" data-testid="reset-done">
          <div className="nxc-card p-4 flex gap-3 items-start"><CheckCircle2 size={20} className="text-emerald-300 shrink-0 mt-0.5" /><div className="text-sm text-slate-200">Password updated for <b>{done}</b>. You can sign in now.</div></div>
          <button onClick={() => nav("/connect", { replace: true })} className="nxc-btn nxc-btn-primary w-full h-12" data-testid="reset-go-login">Sign in</button>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-3" data-testid="reset-form">
          <div className="relative"><input type={show ? "text" : "password"} className="nxc-input pr-11" placeholder="New password" value={pw} onChange={(e) => setPw(e.target.value)} autoComplete="new-password" data-testid="reset-password-input" /><button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500" aria-label="Toggle visibility">{show ? <EyeOff size={16} /> : <Eye size={16} />}</button></div>
          <input type={show ? "text" : "password"} className="nxc-input" placeholder="Confirm new password" value={cf} onChange={(e) => setCf(e.target.value)} autoComplete="new-password" data-testid="reset-confirm-input" />
          <button type="submit" disabled={busy || !pw || !cf} className="nxc-btn nxc-btn-primary w-full h-12" data-testid="reset-submit-button">{busy ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />} Set new password</button>
        </form>
      )}
    </Frame>
  );
};
